import {
    isPhoneNumberE164,
    ReferenceContactId,
    RegionId,
    TaskTypeId,
    toPhoneNumberE164,
} from "@sp-crm/core";
import { useQuery } from "@tanstack/react-query";
import { InputFormBlock } from "components/app/input-form-block";
import { Header } from "components/header";
import { MessageCompose } from "components/messages/message-compose";
import { trpc } from "components/rpc/trpc";
import { AdvancedSearchEntityType } from "generated/graphql";
import React, { useState } from "react";
import { useFeature, useRegionId } from "store/selectors/hooks";
import { usePhoneLines } from "store/selectors/telecom";
import { handleEvent } from "util/user-events";
import { TouchpointRow } from "./touchpoint-row";

interface ReferenceContactMarketingPlanProps {
    contactId: ReferenceContactId;
    variant: "marketingPlan" | "recommendedActions";
}

export const ReferenceContactMarketingPlan: React.FC<
    ReferenceContactMarketingPlanProps
> = ({ contactId, variant }) => {
    const marketingPlaybookEnabled = useFeature("marketingPlaybook");
    const regionId = useRegionId();
    const trpcClient = trpc.useTRPC();
    const phoneLines = usePhoneLines();
    const [composingActivityType, setComposingActivityType] = useState<{
        activityTypeId: TaskTypeId | null;
    } | null>(null);

    const planQuery = useQuery({
        ...trpcClient.marketingPlaybook.contactPlan.queryOptions({
            regionId: regionId ?? ("" as RegionId),
            contactId,
        }),
        enabled: marketingPlaybookEnabled && !!regionId,
    });

    if (!planQuery.isSuccess) {
        return null;
    }
    const plan = planQuery.data;

    const phone =
        plan?.contactPhone && isPhoneNumberE164(plan.contactPhone)
            ? toPhoneNumberE164(plan.contactPhone)
            : null;

    const openCompose = (activityTypeId: TaskTypeId | null) => {
        handleEvent("marketing-plan-send-email");
        setComposingActivityType({ activityTypeId });
    };

    return (
        <>
            <InputFormBlock>
                {variant === "recommendedActions" ? (
                    <Header>Recommended Actions</Header>
                ) : (
                    <Header iconName="25_ActiveTasks">Marketing Plan</Header>
                )}
                {plan ? (
                    <div className="divide-y divide-gray-100">
                        {plan.touchpoints.map(touchpoint => (
                            <TouchpointRow
                                key={touchpoint.touchpointId}
                                touchpoint={{
                                    touchpointId: touchpoint.touchpointId,
                                    targets: touchpoint.targets,
                                    intervalUnit: touchpoint.intervalUnit,
                                    intervalCount: touchpoint.intervalCount,
                                    lastContactedAt: touchpoint.lastContactedAt ?? null,
                                    nextDueAt: touchpoint.nextDueAt ?? null,
                                    state: touchpoint.state,
                                    pendingVisit: touchpoint.pendingVisit,
                                }}
                                contactId={contactId}
                                contactName={plan.contactName}
                                contactEmail={plan.contactEmail}
                                contactEmailOptedOut={plan.contactEmailOptedOut}
                                phone={phone}
                                hasPhoneLine={phoneLines.length > 0}
                                onSendEmail={openCompose}
                            />
                        ))}
                    </div>
                ) : (
                    <p className="text-sm text-gray-500">
                        Set a referral tier to get recommended outreach actions for this
                        contact.
                    </p>
                )}
            </InputFormBlock>
            {composingActivityType ? (
                <MessageCompose
                    entityId={contactId}
                    entityType={AdvancedSearchEntityType.ReferenceContact}
                    activityTypeId={composingActivityType.activityTypeId}
                    onDismiss={() => setComposingActivityType(null)}
                />
            ) : null}
        </>
    );
};
